/**
 * Create / delete WhatsApp templates (#1755).
 *
 * POST and DELETE go through staff-api; Meta is the source of truth for
 * approval. Query invalidation matches the existing sync mutation.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  formatMetaError,
  type WhatsAppTemplateCategory,
  type WhatsAppTemplateLanguage,
} from './templateValidation';
import type { WhatsAppTemplateParameterSource } from '@/lib/whatsappTemplateSources';

/**
 * Meta often returns an empty or unparseable error body. Callers must show a
 * localized fallback when this is empty — never treat a blank string as UI copy.
 */
export function visibleMetaErrorText(metaError: unknown): string {
  try {
    const formatted = formatMetaError(metaError);
    return typeof formatted === 'string' ? formatted.trim() : '';
  } catch {
    return '';
  }
}

export interface CreateWhatsAppTemplateInput {
  name: string;
  language: WhatsAppTemplateLanguage;
  category: WhatsAppTemplateCategory;
  body: string;
  footer?: string;
  examples: string[];
  parameterSources?: (WhatsAppTemplateParameterSource | null)[];
}

export interface CreatedWhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  body: string;
  footer: string | null;
  rejectionReason: string | null;
  createdAt: string;
}

export function useCreateWhatsAppTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWhatsAppTemplateInput) =>
      api.post<{ template: CreatedWhatsAppTemplate }>('/whatsapp/templates', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['whatsapp', 'templates'] });
    },
  });
}

export function useUpdateWhatsAppTemplateParameterSources() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      parameterSources,
    }: {
      id: string;
      parameterSources: (WhatsAppTemplateParameterSource | null)[];
    }) =>
      api.patch(`/whatsapp/templates/${encodeURIComponent(id)}/parameter-sources`, {
        parameterSources,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['whatsapp', 'templates'] });
    },
  });
}

export function useDeleteWhatsAppTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, language }: { name: string; language: string }) =>
      api.delete(`/whatsapp/templates/${encodeURIComponent(name)}`, { params: { language } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['whatsapp', 'templates'] });
    },
  });
}
